import { NoneLabel } from '@/components/labels/NoneLabel';
import { AxleV2Theme } from '@/theme';
import CloseIcon from '@mui/icons-material/Close';
import Edit from '@mui/icons-material/Edit';
import { Box, Button, IconButton, Link, Popover, Typography } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import {
    type ChangeEvent,
    type MutableRefObject,
    type MouseEvent as ReactMouseEvent,
    type ReactNode,
    createContext,
    useCallback,
    useContext,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';

const EXPANDABLE_MAX_LINES = 3;

/**
 * Context to scope "only one popper open at a time" per group.
 * Wrap a set of ExpandableText instances in an ExpandableTextGroup
 * so they close each other but don't interfere with other tables.
 */
const ExpandableTextGroupContext = createContext<{
    closeActive: MutableRefObject<(() => void) | null>;
} | null>(null);

export const ExpandableTextGroup = ({ children }: { children: ReactNode }) => {
    const closeActive = useRef<(() => void) | null>(null);
    return (
        <ExpandableTextGroupContext.Provider value={{ closeActive }}>
            {children}
        </ExpandableTextGroupContext.Provider>
    );
};

interface ExpandableTextProps {
    text: string | null;
    /** Header title shown in the expanded Popper (e.g. "Notes") */
    title?: string;
    /** Subtitle below the title */
    subtitle?: ReactNode;
    /** When provided, enables inline editing with Save/Cancel in the Popper */
    onSave?: (text: string) => Promise<void>;
    /** Preserve newlines (one line per '\n') in the clamped preview and popover, e.g. for lists */
    preserveNewlines?: boolean;
}

export const ExpandableText = ({
    text: textProp,
    title,
    subtitle,
    onSave,
    preserveNewlines = false,
}: ExpandableTextProps) => {
    // Local display text — optimistically updated on save, synced with prop.
    const [localText, setLocalText] = useState(textProp);
    useEffect(() => setLocalText(textProp), [textProp]);
    const text = localText;

    const [isClamped, setIsClamped] = useState(false);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [isEditing, setIsEditing] = useState(false);
    const [draft, setDraft] = useState('');
    const [saving, setSaving] = useState(false);
    const textRef = useRef<HTMLDivElement>(null);
    const open = Boolean(anchorEl);
    const group = useContext(ExpandableTextGroupContext);

    // Stable close function for storing in the group ref.
    const close = useCallback(() => setAnchorEl(null), []);

    // Clear group.closeActive on unmount if this instance was the active one.
    useEffect(() => {
        return () => {
            if (group?.closeActive.current === close) {
                group.closeActive.current = null;
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Apply -webkit- styles on mount. React doesn't handle -webkit- prefixed
    // properties well via JSX style prop, so we set them imperatively.
    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el) {
            return;
        }
        el.style.setProperty('display', '-webkit-box');
        el.style.setProperty('-webkit-box-orient', 'vertical');
        el.style.setProperty('overflow', 'hidden');
        el.style.setProperty('overflow-wrap', 'break-word');
        el.style.setProperty('-webkit-line-clamp', String(EXPANDABLE_MAX_LINES));
        if (preserveNewlines) {
            el.style.setProperty('white-space', 'pre-line');
        }
    }, [preserveNewlines]);

    // Re-measure clamping whenever text changes (e.g. after a save).
    useLayoutEffect(() => {
        const el = textRef.current;
        if (!el) {
            return;
        }
        el.style.setProperty('-webkit-line-clamp', 'unset');
        const fullHeight = el.scrollHeight;
        el.style.setProperty('-webkit-line-clamp', String(EXPANDABLE_MAX_LINES));
        const clampedHeight = el.clientHeight;
        setIsClamped(fullHeight > clampedHeight);
    }, [text]);

    const handleClose = () => {
        close();
        setIsEditing(false);
        if (group) {
            group.closeActive.current = null;
        }
    };

    const handleEdit = () => {
        setDraft(text ?? '');
        setIsEditing(true);
    };

    const openPopper = (anchor: HTMLElement) => {
        group?.closeActive.current?.();
        setAnchorEl(anchor);
        if (group) {
            group.closeActive.current = close;
        }
    };

    const handleInlineEdit = (e: ReactMouseEvent) => {
        e.stopPropagation();
        openPopper(e.currentTarget as HTMLElement);
        handleEdit();
    };

    const handleSave = async () => {
        if (!onSave || draft === text) {
            return;
        }
        setSaving(true);
        try {
            setLocalText(draft);
            await onSave(draft);
            handleClose();
        } catch {
            setLocalText(textProp);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Box
            display={'flex'}
            flexDirection={'column'}
            justifyContent={'flex-start'}
            alignItems={'flex-start'}
            data-testid='expandable-text'
            sx={{
                width: '100%',
                height: '100%',
                '&:hover': {
                    '& .edit-icon': {
                        color: 'text.primary',
                    },
                },
            }}
            onClick={!isClamped && onSave ? handleInlineEdit : undefined}
        >
            <div ref={textRef} style={{ width: '100%' }}>
                {text ? (
                    text
                ) : (
                    <NoneLabel
                        data-testid='notes-none-label'
                        sx={{ display: 'inline', fontSize: 'inherit' }}
                    />
                )}
                {!isClamped && onSave && (
                    <Edit
                        data-testid='notes-edit-icon'
                        fontSize='small'
                        className={'edit-icon'}
                        sx={{
                            color: 'text.disabled',
                            cursor: 'pointer',
                            verticalAlign: 'middle',
                            ml: '3px',
                            display: 'inline',
                        }}
                    />
                )}
            </div>
            {isClamped && (
                <Link
                    component='button'
                    variant='body2'
                    data-testid='notes-expand-toggle'
                    aria-expanded={open}
                    onClick={(e: ReactMouseEvent) => {
                        e.stopPropagation();
                        if (open) {
                            handleClose();
                        } else {
                            openPopper(e.currentTarget as HTMLElement);
                        }
                    }}
                    sx={{ fontSize: 12 }}
                >
                    {open ? 'See less' : 'See more'}
                </Link>
            )}
            {open && (
                <ThemeProvider theme={AxleV2Theme}>
                    <Popover
                        data-testid='expandable-text-popper'
                        open={open}
                        anchorEl={anchorEl}
                        onClose={handleClose}
                        // Popover portals to body, but React synthetic events still
                        // bubble through the component tree — without this, clicks
                        // inside the popover hit the cell-root onClick and re-open
                        // the editor (resetting the draft).
                        onClick={(e: ReactMouseEvent) => e.stopPropagation()}
                        anchorOrigin={{
                            vertical: 'bottom',
                            horizontal: 'right',
                        }}
                        transformOrigin={{
                            vertical: 'top',
                            horizontal: 'right',
                        }}
                        elevation={4}
                        slotProps={{
                            paper: {
                                sx: {
                                    width: 400,
                                    maxHeight: 420,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative',
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                },
                            },
                        }}
                    >
                        {/* Header */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                px: 2,
                                pt: 1.5,
                                pb: title ? 1 : 0,
                            }}
                        >
                            {title && (
                                <Box>
                                    <Typography
                                        sx={{
                                            fontWeight: 700,
                                            fontSize: 16,
                                            lineHeight: 1.3,
                                        }}
                                    >
                                        {title}
                                    </Typography>
                                    {subtitle && (
                                        <Typography
                                            component='div'
                                            sx={{
                                                fontSize: 14,
                                                color: 'text.secondary',
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {subtitle}
                                        </Typography>
                                    )}
                                </Box>
                            )}
                            <IconButton
                                size='small'
                                aria-label='Close'
                                data-testid='notes-popover-close'
                                onClick={(e: ReactMouseEvent) => {
                                    e.stopPropagation();
                                    handleClose();
                                }}
                                sx={{ p: 0.5, ml: 'auto' }}
                            >
                                <CloseIcon fontSize='small' />
                            </IconButton>
                        </Box>

                        {/* Content */}
                        <Box
                            sx={{
                                px: 2,
                                py: 1.5,
                                overflowY: 'auto',
                                fontSize: 14,
                                lineHeight: '20px',
                                wordBreak: 'break-word',
                                textWrap: 'auto',
                                whiteSpace: preserveNewlines ? 'pre-line' : undefined,
                                flexGrow: 1,
                                minHeight: 0,
                            }}
                        >
                            {isEditing ? (
                                <Box
                                    component='textarea'
                                    data-testid='expandable-text-textarea'
                                    value={draft}
                                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) =>
                                        setDraft(e.target.value)
                                    }
                                    autoFocus
                                    sx={{
                                        width: '100%',
                                        minHeight: 140,
                                        resize: 'vertical',
                                        fontSize: 14,
                                        lineHeight: '20px',
                                        fontFamily: 'inherit',
                                        padding: 1.5,
                                        border: '2px solid',
                                        borderColor: 'primary.main',
                                        borderRadius: 1,
                                        outline: 'none',
                                        '&:focus-visible': {
                                            borderColor: 'primary.dark',
                                        },
                                        boxSizing: 'border-box',
                                    }}
                                />
                            ) : (
                                text
                            )}
                        </Box>

                        {/* Footer */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                px: 2,
                                pb: 1.5,
                                pt: 0.5,
                                gap: 1,
                            }}
                        >
                            {isEditing ? (
                                <>
                                    <Button
                                        variant='outlined'
                                        size='small'
                                        data-testid='notes-popover-cancel'
                                        onClick={handleClose}
                                        disabled={saving}
                                    >
                                        Cancel
                                    </Button>
                                    <Button
                                        variant='contained'
                                        size='small'
                                        data-testid='notes-popover-save'
                                        onClick={() => void handleSave()}
                                        disabled={saving || draft === text}
                                        color='primary'
                                    >
                                        Save
                                    </Button>
                                </>
                            ) : (
                                onSave && (
                                    <Button
                                        variant='outlined'
                                        size='small'
                                        data-testid='notes-popover-edit'
                                        onClick={(e: ReactMouseEvent) => {
                                            e.stopPropagation();
                                            handleEdit();
                                        }}
                                    >
                                        Edit
                                    </Button>
                                )
                            )}
                        </Box>
                    </Popover>
                </ThemeProvider>
            )}
        </Box>
    );
};
